// @flow

import React from 'react'
import { Link } from 'gatsby'
import styled from 'styled-components'
import Layout from 'src/components/layout'
import SEO from 'src/components/seo'
import TaglineSection from 'src/components/tagline-section'
import theme from 'src/theme'
import { rhythm, scale } from 'src/typography'

const AppSection = styled.section`
  background: ${props => props.theme.color.contrast.main};
  padding-top: ${rhythm(4)};
  padding-bottom: ${rhythm(2)};
`

const ProseContainer = styled.div`
  width: 50%;
  margin: auto;
  color: ${props => props.theme.color.primary.dark};
  ${props => props.theme.media.phone`
    width: 100%;
    padding-left: ${rhythm(2)};
    padding-right: ${rhythm(2)};
  `}

  h1 {
    color: ${props => props.theme.color.secondary.main};
    ${scale(1.5)};
    line-height: ${rhythm(1.5)};
    word-break: break-word;
    margin-bottom: ${rhythm(1 / 2)};
    ${props => props.theme.media.phone`
      ${scale(3 / 4)};
      line-height: ${rhythm(1)};
    `}
  }

  h3 {
    color: ${props => props.theme.color.secondary.main};
    ${scale(3 / 4)};
    margin-top: ${rhythm(1)};
    margin-bottom: ${rhythm(1 / 2)};
  }

  p,
  li {
    ${scale(1 / 4)};
    font-weight: 300;
  }

  a {
    color: ${props => props.theme.color.primary.main};
  }
`

const SavvasNicholasAdsPage = () => (
  <Layout>
    <SEO
      title='savvasnicholas-ads'
      description='savvasnicholas-ads is a personal command line tool that syncs Google Ads campaigns declared in a source repository to the owner&#39;s own Google Ads manager account through the Google Ads API.'
    />
    <AppSection>
      <TaglineSection
        colors={{
          backgroundColor: theme.color.contrast.main,
          textColor: theme.color.primary.main,
        }}
        crazyTextRange={2}
        align='left'
        title={
          <>
            What
            <br />
            this app
            <br />
            does.
          </>
        }
        subtitle={`A personal command line tool I use to manage my own Google Ads account.`}
      />
      <ProseContainer>
        <h1>savvasnicholas-ads.</h1>

        <p>
          savvasnicholas-ads is a command line tool I built for myself. It syncs
          Google Ads campaign definitions, which I keep as files in a private
          source repository, to my own Google Ads manager account through the
          Google Ads API. Running it makes my Google Ads account match what the
          repository declares.
        </p>

        <h3>Who uses it.</h3>
        <p>
          Only me, Savvas Nicholas. I am the developer of the tool and the owner
          of the Google Ads account it writes to, and I am the only person who
          authorises it. It is a single user internal tool. There are no third
          party users, no sign ups, and no accounts.
        </p>

        <h3>Which Google API it uses.</h3>
        <p>
          The Google Ads API, and nothing else. It reads and writes the campaign
          data in my own Google Ads account. It does not request access to
          Gmail, Drive, Calendar, contacts, or any other Google user data.
        </p>

        <h3>Why it needs OAuth access.</h3>
        <p>
          The Google Ads API only accepts calls made on behalf of a Google
          account that has access to the Ads account. Google OAuth is how I
          grant the script that access as myself, so that it can manage my own
          Google Ads account on my behalf. The tokens it receives are stored
          locally on my own machine and are not sent to any third party or to
          any server operated by this website.
        </p>

        <h3>Privacy.</h3>
        <p>
          The <Link to='/privacy'>privacy policy</Link> sets out exactly what
          this tool and this website do with data, including how the tool meets
          the Google API Services User Data Policy and its Limited Use
          requirements.
        </p>

        <h3>Contact.</h3>
        <p>
          Savvas Nicholas.{' '}
          <a href='mailto:savvas@savvasnicholas.com'>
            savvas@savvasnicholas.com
          </a>
          .
        </p>
      </ProseContainer>
    </AppSection>
  </Layout>
)

export default SavvasNicholasAdsPage
